import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import magazineCover from '@/assets/magazine-cover-2025.jpg';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { RefreshCw } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

// Eager load critical above-the-fold components
import Navigation from '../components/Navigation';
import Hero from '../components/Hero';
import SEO from '../components/SEO';
import Banner from '../components/Banner';
import SubscriptionSection from '../components/shop/SubscriptionSection';
import CurrentIssueSection from '../components/CurrentIssueSection';
import EditorialInterlude from '../components/EditorialInterlude';

// Lazy load below-the-fold components
const FeaturedStories = lazy(() => import('../components/FeaturedStories'));
const Newsletter = lazy(() => import('../components/Newsletter'));
const InstagramFeed = lazy(() => import('../components/InstagramFeed'));
const CommunitySection = lazy(() => import('../components/CommunitySection'));
const SwissRegionsSection = lazy(() => import('../components/SwissRegionsSection'));
const Footer = lazy(() => import('../components/Footer'));

import { useBanners } from '../hooks/useBanners';

// Skeleton loader component
const ComponentSkeleton = () => (
  <div className="py-12 animate-pulse">
    <div className="max-w-7xl mx-auto px-4">
      <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-6"></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-64 bg-gray-200 rounded"></div>
        ))}
      </div>
    </div>
  </div>
);

const Index = () => {
  const { t } = useLanguage();
  const { banners, loading: bannersLoading, trackImpression } = useBanners('homepage');
  
  const handleRefresh = async () => {
    window.location.reload();
  };

  const { isPulling, pullDistance } = usePullToRefresh(handleRefresh);
  
  // Filter banners by type for different placements
  const allTopBanners = banners.filter(b => b.banner_type === 'leaderboard');
  const allSideBanners = banners.filter(b => b.banner_type === 'skyscraper');
  const allContentBanners = banners.filter(b => b.banner_type === 'medium_rectangle');

  // Show only one random banner per type, rotating on each page load
  const randomPick = <T,>(arr: T[]): T | undefined =>
    arr.length > 0 ? arr[Math.floor(Math.random() * arr.length)] : undefined;
  const randomPickTwo = <T,>(arr: T[]): [T | undefined, T | undefined] => {
    if (arr.length === 0) return [undefined, undefined];
    const i = Math.floor(Math.random() * arr.length);
    if (arr.length === 1) return [arr[0], undefined];
    let j = Math.floor(Math.random() * (arr.length - 1));
    if (j >= i) j++;
    return [arr[i], arr[j]];
  };
  const topBanner = React.useMemo(() => randomPick(allTopBanners), [allTopBanners]);
  const [leftSideBanner, rightSideBanner] = React.useMemo(() => randomPickTwo(allSideBanners), [allSideBanners]);
  const contentBanner = React.useMemo(() => randomPick(allContentBanners), [allContentBanners]);

  // Track impressions for visible banners
  React.useEffect(() => {
    const visibleBanners = [topBanner, contentBanner, leftSideBanner, rightSideBanner].filter(Boolean) as typeof banners;
    visibleBanners.forEach(banner => {
      trackImpression(banner.id);
    });
  }, [banners, trackImpression]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "glide – Das Schweizer Langlauf-Magazin",
    "url": "https://glide.swiss",
    "description": "Schweizer Magazin und redaktionelle Plattform für Langlauf und nordischen Skisport.",
    "inLanguage": ["de", "en"],
    "publisher": {
      "@type": "Organization",
      "name": "Rothus Medien AG",
      "logo": {
        "@type": "ImageObject",
        "url": "https://glide.swiss/lovable-uploads/60ad6a07-60fb-4307-af5b-b952dcccde70.png"
      }
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://glide.swiss/stories?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <div className="min-h-screen relative">
      {isPulling && (
        <div 
          className="fixed top-0 left-0 right-0 flex justify-center items-center z-50 transition-all duration-300"
          style={{ 
            transform: `translateY(${Math.min(pullDistance, 80)}px)`,
            opacity: pullDistance / 80 
          }}
        >
          <div className="bg-primary/90 text-primary-foreground px-4 py-2 rounded-full flex items-center gap-2">
            <RefreshCw className={`h-4 w-4 ${pullDistance > 80 ? 'animate-spin' : ''}`} />
            <span className="text-sm">
              {pullDistance > 80 ? t('index.release') : t('index.pull')}
            </span>
          </div>
        </div>
      )}
      
      <SEO
        title="glide – Das Schweizer Langlauf-Magazin"
        description="glide ist das Schweizer Langlauf-Magazin: Reportagen, Loipen, Ausrüstung, Tests und Geschichten aus der nordischen Welt."
        url="https://glide.swiss"
        keywords="Langlauf Schweiz, Nordischer Skisport, Skilanglauf, Loipen Schweiz, Cross Country Skiing, glide Magazin, Wintersport Schweiz"
        structuredData={structuredData}
      />
      <Navigation />
      
      <main className="flex flex-col xl:flex-row">
        {/* Skyscraper Banner - Left Side (hidden on mobile/tablet) */}
        {leftSideBanner && (
          <div className="hidden xl:block xl:w-48 flex-shrink-0">
            <div className="sticky top-20 p-4">
              <Banner
                key={leftSideBanner.id}
                id={leftSideBanner.id}
                title={leftSideBanner.title}
                imageUrl={leftSideBanner.image_url}
                linkUrl={leftSideBanner.link_url}
                bannerType={leftSideBanner.banner_type as 'skyscraper'}
              />
            </div>
          </div>
        )}
        
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <Hero />

          <FeaturedStories />

          <CurrentIssueSection />




          <CommunitySection />

          <SwissRegionsSection />

          {/* Leaderboard Banner */}
          {topBanner && (
            <div className="bg-background py-4 md:py-6 lg:py-8">
              <div className="container mx-auto px-2 sm:px-4 flex justify-center">
                <Banner
                  key={topBanner.id}
                  id={topBanner.id}
                  title={topBanner.title}
                  imageUrl={topBanner.image_url}
                  linkUrl={topBanner.link_url}
                  bannerType={topBanner.banner_type as 'leaderboard'}
                />
              </div>
            </div>
          )}

          <SubscriptionSection />

          {/* Combined editorial + ad block */}
          <section className="bg-ink">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 items-stretch">
              <div className="lg:col-span-8">
                <EditorialInterlude />
              </div>
              {contentBanner && (
                <aside className="lg:col-span-4 flex items-center justify-center bg-ink px-4 py-10 lg:py-0">
                  <Banner
                    key={contentBanner.id}
                    id={contentBanner.id}
                    title={contentBanner.title}
                    imageUrl={contentBanner.image_url}
                    linkUrl={contentBanner.link_url}
                    bannerType={contentBanner.banner_type as 'medium_rectangle'}
                  />
                </aside>
              )}
            </div>
          </section>

          <Newsletter />
          <InstagramFeed username="glide.swiss" />
        </div>

        
        {/* Skyscraper Banner - Right Side (hidden on mobile/tablet) */}
        {rightSideBanner && (
          <div className="hidden xl:block xl:w-48 flex-shrink-0">
            <div className="sticky top-20 p-4">
              <Banner
                key={rightSideBanner.id}
                id={rightSideBanner.id}
                title={rightSideBanner.title}
                imageUrl={rightSideBanner.image_url}
                linkUrl={rightSideBanner.link_url}
                bannerType={rightSideBanner.banner_type as 'skyscraper'}
              />
            </div>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default Index;